import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter,
         HostListener, Inject, OnDestroy, Output, Renderer2, ViewChild
       } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { GalleryConstants } from 'core/constants';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { GalleryImage, GalleryService } from './gallery.service';

interface SwipeDetection
{
  startX: number;
  endX: number;
}

interface Progress
{
  inProgress: boolean;
  current: number;
  total: number;
}

@Component({
    selector: 'gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.scss'],
    standalone: false
})
export class GalleryComponent implements AfterViewInit, OnDestroy
{
  private readonly MAX_DESKTOP_ROWS: number = 3;
  private readonly SWIPE_SENSITIVITY: number = 30;

  private galleryService: GalleryService;
  private placeholderService: PlaceholderService;
  private renderer: Renderer2;
  private sanitizer: DomSanitizer;
  private imageStockChangeSubscription: Subscription;
  private uploadProgressChangeSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private swipeDetection: SwipeDetection = {
    startX: undefined,
    endX: undefined,
  };

  public paginationId: string;
  public galleryImages: Array<Array<GalleryImage>> = [];
  public loadedImages: Array<GalleryImage> = [];
  public showUploadPhotosInfo: boolean = true;
  public trackById: Object = Utils.trackById;

  @Output() public imageClicked: EventEmitter<string> =
      new EventEmitter<string>();
  @ViewChild('imageSelect') public imageSelect: any;
  @ViewChild('thumbnailsWrapper') public thumbnailsWrapper: any;

  public progress: Progress = <Progress> {
    inProgress: false,
    current: 0,
    total: 0,
  };

  public constructor(
      private cdr: ChangeDetectorRef,
      @Inject(Renderer2) renderer: Renderer2,
      @Inject(GalleryService) galleryService: GalleryService,
      @Inject(DomSanitizer) sanitizer: DomSanitizer,
      @Inject(PlaceholderService) placeholderService: PlaceholderService)
  {
    this.renderer = renderer;
    this.galleryService = galleryService;
    this.placeholderService = placeholderService;
    this.sanitizer = sanitizer;
    this.progress.inProgress = this.galleryService.getUploadInProgress();

    this.imageStockChangeSubscription =
        this.galleryService.imageStockChanged.subscribe(() => {
          this.galleryImages = this.getImages();
          this.refreshShowUploadPhotosInfo();
        }, error => {});

    this.uploadProgressChangeSubscription =
        this.galleryService.uploadProgressChanged.subscribe((value) => {
          this.progress = {
            inProgress: value,
            current: this.galleryService.imagesUploaded,
            total: this.galleryService.imagesToUpload,
          };
        });

    this.paginationId = Math.random().toString(36).substr(2, 6);
  }

  public async ngAfterViewInit(): Promise<void>
  {
    await this.waitForView();
    this.galleryImages = this.getImages();
    this.refreshShowUploadPhotosInfo();

    this.cdr.detectChanges();
    this.addPaginationSwipeEventListener();
    this.renderer.setProperty(
        this.thumbnailsWrapper.nativeElement,
        'style',
        `--checkmark-url: url(${Utils.url('./assets/img/checkmark.svg')});
        --loading-url: url(${Utils.url('./assets/img/loading.gif')})`,
    );
  }

  private async waitForView(): Promise<void>
  {
    const RETRY_TIMEOUT = 100; /* ms */

    while (!this.thumbnailsWrapperIsReady()) {
      await Utils.sleep(RETRY_TIMEOUT);
    }
  }

  public dragStart(imageId: string): boolean
  {
    if (this.galleryService.isImageUsed(imageId)) {
      return false;
    }
    this.galleryService.setDraggingImageId(imageId);

    this.placeholderService.disableTextPlaceholders();

    return true;
  }

  public dragOver(event: Event): void
  {
    event.preventDefault();
  }

  public dragEnter(event): void
  {
    const galleryImageId = event.target.getAttribute('data-imageId');
    if (this.isDroppableOnGalleryImage(galleryImageId)) {
      this.renderer.addClass(event.target.parentElement, 'highlight');
    }
  }

  public dragLeave(event): void
  {
    this.renderer.removeClass(event.target.parentElement, 'highlight');
  }

  private getImages(): Array<Array<GalleryImage>>
  {
    const distance = 7; // Margin + Padding + Leerzeichen
    const maxWidth = this.thumbnailsWrapper.nativeElement.offsetWidth;

    const maxGalleryRows = (() => {
      if (Utils.isSmallDevice() || Utils.isMediumDevice()) {
        const viewportHeight = Math.max(
            document.documentElement.clientHeight, window.innerHeight || 0);

        const maxHeight = viewportHeight - 200;

        return Math.max(
          Math.floor(
            maxHeight / (GalleryConstants.THUMB_HEIGHT + 2 * distance)),
          1);
      } else {
        return this.MAX_DESKTOP_ROWS;
      }
    })();

    const thumbnailWidth = (img) =>
        (img.width / img.height) * GalleryConstants.THUMB_HEIGHT + 2 * distance;

    const rowWidth = (row) => Utils.sum(row.map(thumbnailWidth));

    const isNotEmpty = (row) => row.length > 0;

    const groupImagesToRows = Utils.groupByCondition(
        (row, img) => rowWidth(row) + thumbnailWidth(img) <= maxWidth);

    const groupRowsToPages = Utils.groupByCondition(
        (page, row) => page.length < maxGalleryRows);

    const pages = this.galleryService.getImages()
        .reduce(groupImagesToRows, [])
        .filter(isNotEmpty)
        .reduce(groupRowsToPages, []);

    return pages.map(Utils.flattenArray);
  }

  public imageIsUsed(imageId: string): boolean
  {
    return this.galleryService.isImageUsed(imageId);
  }

  public imageIsSelected(imageId: string): boolean
  {
    return this.galleryService.isImageSelected(imageId);
  }

  private isDroppableOnGalleryImage(galleryImageId: string): boolean
  {
    const draggingImageId = this.galleryService.getDraggingImageId();
    return (
        galleryImageId !== draggingImageId &&
        this.galleryService.isImageUsed(draggingImageId) &&
        !this.galleryService.isImageUsed(galleryImageId));
  }

  private paginationSwipeTouchStart(event: TouchEvent): void
  {
    const touch = event.touches[0];
    this.swipeDetection.startX = touch.screenX;
  }

  private paginationSwipeTouchMove(event: TouchEvent): void
  {
    const touch = event.touches[0];
    this.swipeDetection.endX = touch.screenX;
  }

  private paginationSwipeTouchEnd(event: Event): void
  {
    let currentPage = this.currentPage();
    const deltaX = this.swipeDetection.startX - this.swipeDetection.endX;
    if (Math.abs(deltaX) > this.SWIPE_SENSITIVITY) {
      if (deltaX < 0 && currentPage > 1) {
        currentPage --;
      }
      else if (deltaX > 0 && currentPage < this.galleryImages.length) {
        currentPage ++;
      }
      this.pageChanged(currentPage);
      this.resetSwipeDetection();
    }
  }

  private addPaginationSwipeEventListener(): void
  {
    const wrapper = this.thumbnailsWrapper.nativeElement;
    wrapper.addEventListener('touchstart',
        this.paginationSwipeTouchStart.bind(this), { passive: true });
    wrapper.addEventListener('touchmove',
        this.paginationSwipeTouchMove.bind(this), { passive: true });
    wrapper.addEventListener(
        'touchend', this.paginationSwipeTouchEnd.bind(this), { passive: true });
  }

  private removePaginationSwipeEventListener(): void
  {
    const wrapper = this.thumbnailsWrapper.nativeElement;
    wrapper.removeEventListener(
        'touchstart', this.paginationSwipeTouchStart.bind(this));
    wrapper.removeEventListener(
        'touchmove', this.paginationSwipeTouchMove.bind(this));
    wrapper.removeEventListener(
        'touchend', this.paginationSwipeTouchEnd.bind(this));
  }

  private resetSwipeDetection(): void
  {
    this.swipeDetection.startX = undefined;
    this.swipeDetection.endX = undefined;
  }

  private thumbnailsWrapperIsReady(): boolean
  {
    return this.thumbnailsWrapper &&
        this.thumbnailsWrapper.nativeElement.offsetWidth > 0;
  }

  public ngOnDestroy(): void
  {
    this.imageStockChangeSubscription.unsubscribe();
    this.galleryService.uploadProgressChanged.unsubscribe(
        this.uploadProgressChangeSubscription);

    this.removePaginationSwipeEventListener();
  }

  public pageChanged(page: number): void
  {
    this.galleryService.lastVisitedPage = page;
  }

  public currentPage(): number
  {
    return this.galleryService.lastVisitedPage;
  }

  @HostListener('dragend', ['$event'])
  public dragEnd(event: Event): void
  {
    this.galleryService.setDraggingImageId(undefined);
    this.galleryService.setSelectedImageId(undefined);

    for (let iph of this.placeholderService.getImagePlaceholders(true)) {
      iph.hidePlaceholderHighlighter();
    }

    this.placeholderService.enableTextPlaceholders();
  }

  @HostListener('window.resize', ['$event'])
  public onResize(): void
  {
    if (this.thumbnailsWrapperIsReady()) {
      this.galleryImages = this.getImages();
    }
  }

  public getThumbnailUrl(info: GalleryImage): string
  {
    return `${info.url}?thumbnail`;
  }

  public getThumbnailWidth(info: GalleryImage): SafeHtml
  {
    const aspect = info.width / info.height;

    return this.sanitizer.bypassSecurityTrustStyle(
        `${aspect * GalleryConstants.THUMB_HEIGHT}px`);
  }

  public getThumbnailHeight(): SafeHtml
  {
    return this.sanitizer.bypassSecurityTrustStyle(
        `${GalleryConstants.THUMB_HEIGHT}px`);
  }

  public refreshShowUploadPhotosInfo(): void
  {
    this.showUploadPhotosInfo =
        this.galleryImages.length < 1 &&
        !(Utils.isSmallDevice() || Utils.isMediumDevice());
  }
}
